import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import moment from 'moment';
import { PluginFormProps, PluginFormData, DEFAULT_STATUS_OPTIONS, defaultStatusToDiscrete, discreteToDefaultStatusOption } from './types';
import { styles } from './styles';
import { Modal, Button, Input, Select, LoadingBlock, Alert, Checkbox } from '@v2/components';
import { useManagePlugin, usePluginVersions } from '@v2/hooks/plugins';
import { usePims } from '@v2/hooks/pims/usePims';
import { AddPimsDependencyModal } from './components/AddPimsDependencyModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import type { PluginPimsDependency } from '@v2/types';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[4][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
function isUUIDFormat(str: string): boolean {
  return UUID_REGEX.test(str);
}

const PluginForm: React.FC<PluginFormProps> = ({
  plugin,
  existingPluginIds = [],
  onCancel,
  onCompleted,
}) => {
  const isEditing = !!plugin;
  const title = isEditing ? 'Edit Plugin' : 'Create Plugin';

  const [addPimsModalOpen, setAddPimsModalOpen] = useState(false);
  const { managePlugin, loading: manageLoading, error: manageError } = useManagePlugin();
  const { versions, loading: versionsLoading } = usePluginVersions(isEditing ? plugin?.id ?? null : null);
  const { pims } = usePims();

  const sortedVersions = useMemo(
    () =>
      [...(versions ?? [])].sort(
        (a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()
      ),
    [versions]
  );

  const defaultVersionOptions =
    plugin?.id && sortedVersions.length
      ? sortedVersions.map((v) => ({
          value: v.id,
          label: `${v.version} (${v.releaseDate})`,
        }))
      : [];

  const defaultStatusOptions = useMemo(
    () => DEFAULT_STATUS_OPTIONS.map((opt) => ({ value: opt, label: opt })),
    []
  );

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<PluginFormData>({
    defaultValues: {
      pluginId: '',
      name: '',
      defaultVersionId: '',
      defaultStartupTimeout: '0',
      defaultStatus: '',
      isInactive: false,
      scheduledReleaseEnabled: false,
      scheduledReleaseTime: '',
      pimsDependencies: [],
    },
  });

  const pimsDependencies = watch('pimsDependencies');

  useEffect(() => {
    if (plugin) {
      reset({
        pluginId: plugin.id,
        name: plugin.name ?? '',
        defaultVersionId: plugin.defaultVersion?.id ?? '',
        defaultStartupTimeout: String(plugin.defaultStartupTimeout ?? 0),
        defaultStatus: discreteToDefaultStatusOption(plugin.defaultStatus),
        isInactive: plugin.isInactive ?? false,
        scheduledReleaseEnabled: !!plugin.soonestScheduledRelease,
        scheduledReleaseTime: plugin.soonestScheduledRelease ?? '',
        pimsDependencies: plugin.pimsDependencies ?? [],
      });
    }
  }, [plugin, reset]);

  const onSubmit = async (data: PluginFormData) => {
    const defaultStatus = defaultStatusToDiscrete(data.defaultStatus);
    const startupTimeout = Number(data.defaultStartupTimeout);
    const releaseTimeFormatted =
      data.scheduledReleaseEnabled && data.scheduledReleaseTime
        ? moment(data.scheduledReleaseTime).utc().format('YYYY-MM-DD HH:mm:ss')
        : null;

    if (isEditing && plugin) {
      const payload = {
        id: plugin.id,
        name: data.name,
        defaultVersionId: data.defaultVersionId || null,
        defaultStartupTimeout: startupTimeout,
        pimsDependencies: data.pimsDependencies,
        defaultStatus,
        isInactive: data.isInactive,
        releaseTime: releaseTimeFormatted,
      };
      const updated = await managePlugin(payload);
      if (updated) {
        toast.success('Plugin updated successfully');
        onCompleted(updated.id);
      } else {
        toast.error(manageError ?? 'Failed to update plugin');
      }
    } else {
      const payload = {
        id: (data.pluginId?.trim() || crypto.randomUUID()) as string,
        name: data.name,
        defaultVersionId: null,
        defaultStartupTimeout: startupTimeout,
        pimsDependencies: data.pimsDependencies,
        isInactive: false,
        defaultStatus,
      };
      const created = await managePlugin(payload);
      if (created) {
        toast.success('Plugin created successfully');
        onCompleted(created.id);
      } else {
        toast.error(manageError ?? 'Failed to create plugin');
      }
    }
  };

  const handleAddPimsDependency = (pimsId: string, minimumPimsVersionId: string | null) => {
    const current = watch('pimsDependencies');
    if (current.some((d) => d.pimsId === pimsId && d.minimumPimsVersionId === minimumPimsVersionId))
      return;
    setValue('pimsDependencies', [...current, { pimsId, minimumPimsVersionId }], { shouldDirty: true });
  };

  const handleRemovePimsDependency = (index: number) => {
    const current = watch('pimsDependencies');
    setValue(
      'pimsDependencies',
      current.filter((_, i) => i !== index),
      { shouldDirty: true }
    );
  };

  const isLoading = manageLoading || isSubmitting;
  const submitError = manageError;
  const initialLoading = isEditing && versionsLoading;

  const currentDefaultVersionId = watch('defaultVersionId');
  const currentDefaultStatusOption = watch('defaultStatus');
  const scheduleReleaseDisabled =
    isEditing &&
    !!plugin &&
    (currentDefaultVersionId || '') === (plugin.defaultVersion?.id || '') &&
    defaultStatusToDiscrete(currentDefaultStatusOption).isCached === plugin.defaultStatus?.isCached &&
    defaultStatusToDiscrete(currentDefaultStatusOption).isInstalled === plugin.defaultStatus?.isInstalled &&
    defaultStatusToDiscrete(currentDefaultStatusOption).isRunning === plugin.defaultStatus?.isRunning;

  const tz = moment().tz(moment.tz.guess()).zoneAbbr();

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      title={title}
      size="50vw"
      variant="side"
      footer={
        <>
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={!isDirty || isLoading}
            isLoading={isLoading}
          >
            {isEditing ? 'Save Changes' : 'Create Plugin'}
          </Button>
        </>
      }
    >
      {initialLoading ? (
        <LoadingBlock className={styles.loadingContainer} />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className={styles.formContainer}>
          {submitError && <Alert variant="error">{submitError}</Alert>}

          <div className={styles.fullWidthField}>
            <Input
              {...register('name', { required: 'Plugin name is required' })}
              label="Plugin Name"
              placeholder="Plugin name"
              error={errors.name?.message}
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.fullWidthField}>
              <Input
                {...register('pluginId', {
                  required: false,
                  validate: (v) => {
                    if (isEditing) return true;
                    const id = (v ?? '').trim();
                    if (id === '') return true;
                    if (!isUUIDFormat(id)) return 'Invalid UUID format or is an ID for an existing plugin.';
                    if (existingPluginIds.includes(id)) return 'Invalid UUID format or is an ID for an existing plugin.';
                    return true;
                  },
                })}
                label="Plugin ID"
                placeholder="e.g. UUID"
                disabled={isEditing}
                error={errors.pluginId?.message}
                hint={!isEditing ? 'Id will be generated if not provided' : undefined}
              />
            </div>
            <Input
              {...register('defaultStartupTimeout', {
                required: 'Required',
                validate: (v) => (!Number.isNaN(Number(v)) && Number(v) >= 0) || 'Default startup timeout must be a number.',
              })}
              label="Default Startup Time (seconds)"
              placeholder="0"
              type="number"
              min={0}
              error={errors.defaultStartupTimeout?.message}
            />
          </div>

          <div className={styles.formRow}>
            <div className={!isEditing ? styles.fullWidthField : undefined}>
              <Select
                {...register('defaultStatus', { required: 'Default status is required' })}
                label="Default Status"
                options={defaultStatusOptions}
                placeholder={!isEditing ? '--Select a status--' : undefined}
                error={errors.defaultStatus?.message}
                aria-label="Default status"
              />
            </div>
            {isEditing && (
              <div className={styles.selectWrapper}>
                <Select
                  {...register('defaultVersionId')}
                  label="Default Plugin Version"
                  options={[{ value: '', label: '--No default version set--' }, ...defaultVersionOptions]}
                  disabled={defaultVersionOptions.length === 0}
                  aria-label="Default plugin version"
                />
              </div>
            )}
          </div>

          <div className={styles.fullWidthField}>
            <div className={styles.pimsHeader}>
              <label className={styles.pimsHeaderLabel}>
                PIMS Dependencies
              </label>
              <Button
                type="button"
                variant="ghost-success"
                size="sm"
                onClick={() => setAddPimsModalOpen(true)}
                icon={<FontAwesomeIcon icon={faPlus} />}
              >
                Add PIMS Dependency
              </Button>
            </div>
            <div className={styles.pimsList}>
              {(pimsDependencies as PluginPimsDependency[]).map((dep, index) => {
                const pimsName = pims.find((p) => p.id === dep.pimsId)?.name ?? dep.pimsId;
                return (
                  <div key={`${dep.pimsId}-${index}`} className={styles.pimsRow}>
                    <span className={styles.pimsRowLabel}>
                      {pimsName}
                      {dep.minimumPimsVersionId ? ` (min: ${dep.minimumPimsVersionId})` : ''}
                    </span>
                    <Button
                      type="button"
                      variant="ghost-danger"
                      size="sm"
                      onClick={() => handleRemovePimsDependency(index)}
                      aria-label={`Remove ${pimsName}`}
                      icon={<FontAwesomeIcon icon={faTrash} />}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {isEditing && (
            <>
              {plugin?.soonestScheduledRelease && (
                <Alert variant="warning" className={styles.scheduleAlert}>
                  This plugin already has a scheduled release time of{' '}
                  {moment
                    .utc(plugin.soonestScheduledRelease)
                    .local()
                    .format('M/D/YYYY LT')}{' '}
                  {tz}
                </Alert>
              )}
              <div className={styles.formRow}>
                <Checkbox
                  label="Is Active"
                  checked={!watch('isInactive')}
                  onChange={(checked) => setValue('isInactive', !checked, { shouldDirty: true })}
                />
                <Checkbox
                  label="Schedule Release?"
                  checked={watch('scheduledReleaseEnabled')}
                  onChange={(checked) => setValue('scheduledReleaseEnabled', checked, { shouldDirty: true })}
                  disabled={scheduleReleaseDisabled}
                />
              </div>
              {watch('scheduledReleaseEnabled') && (
                <div className={styles.fullWidthField}>
                  <Input
                    {...register('scheduledReleaseTime')}
                    label={`Scheduled Release Time (${tz})`}
                    placeholder="YYYY-MM-DD HH:mm:ss"
                    type="datetime-local"
                  />
                </div>
              )}
            </>
          )}
        </form>
      )}

      <AddPimsDependencyModal
        isOpen={addPimsModalOpen}
        onClose={() => setAddPimsModalOpen(false)}
        onAdd={handleAddPimsDependency}
        pims={pims}
        existingPimsIds={[]}
      />
    </Modal>
  );
};

export default PluginForm;
export type { PluginFormProps, PluginFormData } from './types';
